import { Box, Paper, Typography } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import { isolateNumber } from '@/utils/numberDirection'

// Format number — compact large values (e.g. 17299 → 17.3K), otherwise 2 decimals
const formatNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return String(value)
  const abs = Math.abs(num)
  if (abs >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
  if (abs >= 10_000) return (num / 1_000).toFixed(1) + 'K'
  return num.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function KpiCard({
  title,
  value,
  unit = 'קוב',
  dateRangeText,
  blue = false,
}: {
  title: string
  value: number | string
  unit?: string
  dateRangeText?: string
  blue?: boolean
}) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    bg: isDarkMode ? '#162645' : '#FFFFFF',
    border: isDarkMode ? '#445876' : '#E6ECF2',
    valueText: isDarkMode ? '#fff' : '#003F61',
    unitText: isDarkMode ? '#93A3B5' : '#6B829E',
    titleText: isDarkMode ? '#B0BEC5' : '#0F3E66',
    dateText: isDarkMode ? '#6B7A8C' : '#8F98A5',
  }

  // Format the display value
  const displayValue = formatNumber(value)

  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 0px',
        minWidth: 0,
        borderRadius: 2,
        px: { xs: 2, lg: 1.25, xl: 1.5 },
        py: 1,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.bg,
        overflow: 'hidden',
      }}
      dir="rtl"
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography
          sx={{
            color: colors.valueText,
            fontWeight: 700,
            fontSize: { xs: '29.84px', lg: '20px', xl: '24px' },
            fontFamily: 'Rubik, sans-serif',
          }}
        >
          {isolateNumber(displayValue)}
        </Typography>
        <Typography
          sx={{
            color: colors.unitText,
            fontFamily: 'Rubik, sans-serif',
            flexShrink: 0,
          }}
        >
          {unit}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          sx={{
            color: colors.titleText,
            fontWeight: 700,
            fontSize: '13px',
            fontFamily: 'Rubik, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: blue ? '#254D88' : '#B3E084',
            flexShrink: 0,
          }}
        />
        {dateRangeText && (
          <Typography
            sx={{
              color: colors.dateText,
              fontSize: '10px',
              fontFamily: 'Rubik, sans-serif',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {dateRangeText}
          </Typography>
        )}
      </Box>
    </Paper>
  )
}

export default function KpiResults({
  totalM3,
  hourlyAvgM3,
  isSame = false,
  title = 'סך צריכה',
  firstDateRangeText = 'צ. יום',
  secondDateRangeText = 'צ. לילה',
  firstTitle = 'סך צריכה',
  secondTitle = 'ממוצע צריכה',
}: {
  totalM3: number | string
  hourlyAvgM3: number | string
  isSame?: boolean
  title?: string
  firstDateRangeText?: string
  secondDateRangeText?: string
  firstTitle?: string
  secondTitle?: string
}) {
  return (
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        gap: { xs: 2, lg: 1, xl: 2 },
        flexDirection: 'row',
      }}
    >
      <KpiCard
        title={firstTitle || title || 'סך צריכה'}
        value={hourlyAvgM3}
        dateRangeText={firstDateRangeText}
      />
      <KpiCard
        title={secondTitle || (isSame ? 'סך צריכה' : 'ממוצע צריכה')}
        value={totalM3}
        dateRangeText={secondDateRangeText || 'צ. לילה'}
        blue
      />
    </Box>
  )
}
